import React from 'react';
import orderBy from 'lodash/orderBy';
import { format } from 'date-fns';
import utcToZonedDate from '@/Framework/DateTime/utcToZonedDate';
import { MONTH_DAY_YEAR_FORMAT } from '@/Framework/DateTime/dateFormats';
import { IIndustry, IJointLead, ILead, ISubindustry } from '@/finsight/domain/vo/IDeal';
import { IDatetime } from '@/Framework/DateTime/Datetime';
import IDealHeader from '@/finsight/ui/components/deals/DealsList/DealHeader/IDealHeader';
import {
  getDealRoute,
  getIndustryRoute,
  getParentRoute,
  getSectorRoute,
} from '@/finsight/Router/RouteWithRedirect/getNewRoute';
import isServer from '@/Framework/Router/Next/isServer';
import formatNumber from '@/Framework/dataHelpers/formatters/formatNumber';
import ISector from '@/finsight/domain/vo/ISector';

import spacesStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';

export function getLeadsForEllipsesTooltipWidth(structuringLeads: ILead[], jointLeads: ILead[], isLast) : string {
  let jointLeadsText = `Joint Lead${ jointLeads.length <= 1 ? '' : 's' }: `;
  let maximumWidths = {
    n: 300,
    x: 400,
    m: 550,
    l: 700,
    xl: 850,
    xxl: 1000,
  };

  let jointLeadsWidth = measureTextSize(jointLeads, jointLeadsText).width;
  let maxWidth = null;
  let padding = 30;
  let rowsCount = !!structuringLeads.length || isLast ? 2 : 3;

  Object.keys(maximumWidths).forEach((size) => {
    if ((jointLeadsWidth / (maximumWidths[size] - padding)) <= rowsCount && !maxWidth) {
      maxWidth = maximumWidths[size];
    }
  });

  return maxWidth ? `${ maxWidth }px` : '300px';
}

/**
 * Measure real text size inside invisible out-of-viewport temporary div
 */
export function measureTextSize(leads: ILead[], baseText: string) : { width: number, height: number } {
  let lDiv = document.createElement('div');

  document.body.appendChild(lDiv);

  lDiv.style.fontSize = '12px';
  lDiv.style.position = 'absolute';
  lDiv.style['white-space'] = 'nowrap';
  lDiv.style.left = '-1000px';
  lDiv.style.top = '-1000px';

  lDiv.innerHTML = baseText + getLeadsTextForEllipsesTooltip(leads);

  let lResult = {
    width: lDiv.clientWidth,
    height: lDiv.clientHeight,
  };

  document.body.removeChild(lDiv);
  lDiv = null;

  return lResult;
}

export function getLeadsTextForEllipsesTooltip(leads: ILead[]) : string {
  return leads.map(({ name }) => name).join(', ');
}

export function getLeadsForEllipsesTooltip(structuringLeads: ILead[], jointLeads: ILead[]): JSX.Element {
  return (
    <span>
      { !!structuringLeads.length && (
        <span key="structuring-lead">
          <span className={ spacesStyles.mrx }>Structuring Lead{ structuringLeads.length <= 1 ? '' : 's' }:</span>
          <span>
            { getLeadsTextForEllipsesTooltip(structuringLeads) }
          </span>
        </span>
      ) }
      { !!jointLeads.length && (
        <span key="joint-lead">
          { !!structuringLeads.length && <br /> }
          <span className={ spacesStyles.mrx }>Joint Lead{ jointLeads.length <= 1 ? '' : 's' }:</span>
          <span>
            { getLeadsTextForEllipsesTooltip(jointLeads) }
          </span>
        </span>
      ) }
    </span>
  );
}

export function getDate(dateObject: IDatetime): string {
  if (!dateObject) {
    return '';
  }
  const { timezone, dateTime } = dateObject;
  return format(utcToZonedDate(timezone.timeZone, new Date(dateTime)), MONTH_DAY_YEAR_FORMAT);
}

export function getSectorIndustryLink(deal: IDealHeader): { pathname: string, search: string } | string {
  let pathname = '';
  if (deal.dealType === 'industry') {
    if (!deal.parent.industry) {
      return '#';
    }
    pathname = !isServer() ? getIndustryRoute(deal.parent.industry.name, deal.parent.subindustry.name) : '';
  } else {
    pathname = !isServer() ? getSectorRoute(deal.sector.name, deal.subsector.name) : '';
  }

  return {
    pathname,
    search: `products=${ deal.product.abbreviation }&regions=${ deal.region.abbreviation }`,
  };
}

export function mapDealFields(deal): IDealHeader {
  deal.structuringLeads = getSortedLeads(deal.structuringLeads);
  deal.jointLeads = getSortedLeads(deal.jointLeads);

  // map sector/subsector OR industry/subindustry
  deal.dealTypeObject = getSectorIndustryByDeal(deal);
  deal.dealTypeSubObject = getSubsectorSubindustryByDeal(deal);

  deal.region = deal.country?.region;

  // map links
  deal.sectorIndustryLink = getSectorIndustryLink(deal);
  deal.parentLink = getParentRoute(deal.parent);
  deal.link = getDealRoute(deal);

  // If deal has only one currency - display it formatted
  if (Object.keys(deal.totalSize).length === 1) {
    // @ts-ignore
    const currency: {
      size: number,
      displayName: string,
    } = Object.values(deal.totalSize)[0];

    const size = formatNumber(currency.size, {
      symbolOnLeft: true,
      symbol: currency.displayName,
      spaceBetweenValueAndSymbol: true,
    });

    deal.totalSize = `(${ size }m)`;
  } else {
    deal.totalSize = '';
  }

  return deal;
}

function getSortedLeads <T extends ILead | IJointLead>(leads: T[]): T[] {
  if (leads.length) {
    return orderBy(leads, (lead) => lead.name);
  }
  return leads;
}

function getSectorIndustryByDeal(deal: IDealHeader) : IIndustry | ISector | { name: string, abbreviation: string } {
  if (deal.dealType === 'sector') {
    return deal.sector;
  }

  return deal.parent.industry || {
    name: '',
    abbreviation: '',
  };
}

function getSubsectorSubindustryByDeal(deal: IDealHeader) : ISector | ISubindustry | { name : string } {
  if (deal.dealType === 'sector') {
    return deal.subsector;
  }

  return deal.parent.subindustry || {
    name: '',
  };
}
